import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'

import { useLanguage } from '@orus.eu/pharaoh'

import type { StandaloneFormAccess } from '../../standalone-form-access.types.ts'
import type { StandaloneFormSubmitter } from '../../standalone-form-entry.types.ts'

import {
  nlAovqlHealthAccess,
  nlAovqlHealthSubmitResult,
  type NlAovqlHealthContext,
  type NlAovqlHealthFormResult,
} from './nl-aovql-health-page-state.utils.ts'

import { useTRPC } from '#/client.ts'
import { frontOfficeQueryOptions } from '#/shared/front-office-query-options.ts'
import type { RouterContext } from '#/shared/routing/router-context.ts'

/** Loads the context, for the route loader, and tells whether the customer may take the form now. */
export async function ensureNlAovqlHealthFormAccess(
  { queryClient, trpc }: RouterContext,
  subscriptionId: string,
): Promise<StandaloneFormAccess> {
  const [access] = await Promise.all([
    queryClient.fetchQuery(trpc.nlAovqlHealthForm.getFormAccess.queryOptions({ subscriptionId })),
    // Read by the flow: loaded here so the page does not suspend on it
    queryClient.ensureQueryData(
      trpc.nlAovqlHealthForm.getCurrentState.queryOptions({ subscriptionId }, frontOfficeQueryOptions),
    ),
  ])
  return nlAovqlHealthAccess(access)
}

export function useNlAovqlHealthContext(subscriptionId: string): NlAovqlHealthContext {
  const trpc = useTRPC()
  const { data } = useSuspenseQuery(
    trpc.nlAovqlHealthForm.getCurrentState.queryOptions({ subscriptionId }, frontOfficeQueryOptions),
  )
  return data
}

export function useNlAovqlHealthSubmitter(subscriptionId: string): StandaloneFormSubmitter<NlAovqlHealthFormResult> {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  // The language the customer read the questions in and answered in, which the declaration PDF follows
  const language = useLanguage()
  const { mutate, isPending } = useMutation({
    ...trpc.nlAovqlHealthForm.submit.mutationOptions(),
    // The gate step reads the status from the context, so it must not serve the one before the submission
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: trpc.nlAovqlHealthForm.getCurrentState.queryKey({ subscriptionId }),
        refetchType: 'all',
      }),
  })

  return {
    submit: ({ definitionVersion, ...submission }, { onSubmitted, onFailed }) =>
      mutate(
        { ...submission, healthFormVersion: definitionVersion, language },
        {
          onSuccess: (output) => onSubmitted(nlAovqlHealthSubmitResult(output)),
          onError: onFailed,
        },
      ),
    submitting: isPending,
  }
}
